import React, { useEffect, useState } from 'react'
import FileImport from '../../GlobalComponents/FileImport/FileImport'
import './VendorInventoryUpload.scss'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { DatePicker, DATE_INPUT_POPUP_TYPES } from '../../GlobalComponents/DateInput/DateInput'
import { AWCButton, BUTTON_TYPES } from '../../GlobalComponents/Inputs/Inputs'
import { v4 as uuidv4 } from 'uuid'
import { fetchBatchQueueSubmit, fetchPostBatchFile } from './VendorInventoryAPI'
import { User, fetchGetCurrentUser } from '../../utils'
import { LOADING_STATES } from '../../features/meta/metaSlice'
import {
    NotificationManager,
    fqAlert,
} from '../../GlobalComponents/NotificationManager/NotificationManager'

export const VendorInventoryUpload = () => {
    const [vendorID, setVendorID] = useState<string>('Choose One...')
    const [priceEffectiveDate, setPriceEffectiveDate] = useState<Date>()

    const [batchGroupGUID, setBatchGroupGUID] = useState<string>(uuidv4())
    const [fileData, setFileData] = useState<string>('')
    const [fileName, setFileName] = useState<string>('')

    const [isValidVendor, setIsValidVendor] = useState<boolean>(false)
    const [isValidDate, setIsValidDate] = useState<boolean>(true)
    const [isValidFile, setIsValidFile] = useState<boolean>(false)
    const [importWaitingStatus, setImportWaitingStatus] = useState<LOADING_STATES>(
        LOADING_STATES.INIT
    )

    const [currentUser, setCurrentUser] = useState<User>()

    const fetchCurrentUser = async () => {
        let tempUser = await fetchGetCurrentUser()
        setCurrentUser(tempUser)
    }

    const resetScreen = () => {
        // Reset
        setVendorID('Choose One...')
        setPriceEffectiveDate(undefined)
        setBatchGroupGUID(uuidv4())
        setFileData('')
        setFileName('')

        // Reset validation
        setIsValidVendor(false)
        setIsValidDate(true)
        setIsValidFile(false)
        setImportWaitingStatus(LOADING_STATES.INIT)
    }

    useEffect(() => {
        fetchCurrentUser()
    }, [])

    useEffect(() => {
        if (fileData) {
            fetchPostBatchFile({
                dataURI: fileData,
                batchGroupGUID: batchGroupGUID,
                filename: fileName,
            })
                .then(() => setImportWaitingStatus(LOADING_STATES.DONE))
                .catch((err) => {
                    fqAlert({
                        type: 'error',
                        title: '',
                        message: err.message,
                    })
                    setImportWaitingStatus(LOADING_STATES.INIT)
                })
        }
    }, [fileData])

    return (
        <div className='vendorInventoryUpload'>
            <div
                style={{
                    textAlign: 'center',
                    marginBottom: 2,
                    marginTop: 50,
                    fontSize: 18,
                    fontWeight: 700,
                }}
            >
                Vendor Inventory File Upload
            </div>
            <div className='flexBox'>
                <div className='labelBold'>Vendor ID</div>
                <DropDownList
                    data={['BN', 'PX', 'RT', 'SC', 'SK', 'TU']}
                    defaultValue={'Choose One...'}
                    value={vendorID}
                    onChange={(event: any) => {
                        if (
                            event.value === 'BN' ||
                            event.value === 'RT' ||
                            event.value === 'PX' ||
                            event.value === 'SK'
                        ) {
                            setIsValidVendor(true)
                            setIsValidDate(false)
                        } else if (event.value === 'SC' || event.value === 'TU') {
                            setIsValidVendor(true)
                            setIsValidDate(true)
                        }
                        setVendorID(event.value)
                        setPriceEffectiveDate(undefined)
                    }}
                    style={{ width: 200 }}
                />
            </div>
            {/* Only show this if they choose PX/SK */}
            {(vendorID === 'BN' || vendorID === 'PX' || vendorID === 'RT' || vendorID === 'SK') && (
                <div className='flexBox'>
                    <div className='labelBold'>Price Effective Date</div>
                    <DatePicker
                        popupType={DATE_INPUT_POPUP_TYPES.MODAL}
                        minDate={new Date(2023, 1, 1)}
                        value={priceEffectiveDate}
                        onChange={(newDate: any) => {
                            setPriceEffectiveDate(newDate)
                            setIsValidDate(true)
                        }}
                        onValidityChange={(valid: any) =>
                            valid ? setIsValidDate(true) : setIsValidDate(false)
                        }
                    />
                </div>
            )}
            {(() => {
                switch (importWaitingStatus) {
                    case LOADING_STATES.INIT:
                        return (
                            <div className={'vendorFileUploadBox'}>
                                <FileImport
                                    onDrop={(b64FileData) => {
                                        setImportWaitingStatus(LOADING_STATES.LOADING)
                                        setFileData('data:text/plain;base64,' + b64FileData)
                                        setIsValidFile(true)
                                    }}
                                    fileTypes={['xlsx', 'txt', 'csv']}
                                    fileSizeLimit={50000000}
                                    minHeight={200}
                                    changeBackground={true}
                                    setFileName={setFileName}
                                />
                            </div>
                        )
                    case LOADING_STATES.LOADING:
                        /* Show loading animation if uploading */
                        return (
                            <div
                                style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    zIndex: 10,
                                }}
                            >
                                <div className={'centered'} style={{ height: '100%' }}>
                                    <div
                                        className={'k-i-loading'}
                                        style={{ fontSize: 100, height: '100px' }}
                                    />
                                </div>
                            </div>
                        )
                    case LOADING_STATES.DONE:
                        return (
                            <>
                                <div className={'vendorFileData'}>
                                    {fileName}
                                    <div
                                        className='k-icon k-i-close'
                                        style={{
                                            fontSize: 22,
                                            marginLeft: 5,
                                            marginBottom: 5,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            setImportWaitingStatus(LOADING_STATES.INIT)
                                            setFileData('')
                                            setFileName('')
                                            setIsValidFile(false)
                                        }}
                                    />
                                </div>
                                {/* Gray out submit button if required fields are not filled out */}
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <AWCButton
                                        type={
                                            isValidVendor && isValidDate && isValidFile
                                                ? BUTTON_TYPES.PRIMARY
                                                : BUTTON_TYPES.DISABLED
                                        }
                                        onClick={
                                            isValidVendor && isValidDate && isValidFile
                                                ? async () => {
                                                      let response = await fetchBatchQueueSubmit({
                                                          ProgDomain: 'Inventory',
                                                          ProgName: 'MasterBatch',
                                                          Params: `VENDMSTFILEUPLOAD|${
                                                              currentUser!.CompanyCode || 'A'
                                                          }|${vendorID}|${
                                                              priceEffectiveDate
                                                                  ? priceEffectiveDate.toLocaleDateString(
                                                                        'en-US',
                                                                        {
                                                                            month: '2-digit',
                                                                            day: '2-digit',
                                                                            year: 'numeric',
                                                                        }
                                                                    )
                                                                  : ''
                                                          }|${batchGroupGUID}`,
                                                          Username: currentUser!.Username,
                                                          Email: currentUser!.Email,
                                                          Crystal: false,
                                                      })
                                                      alert(response)
                                                      resetScreen()
                                                  }
                                                : () => {}
                                        }
                                        style={{ marginTop: 20 }}
                                    >
                                        Submit
                                    </AWCButton>
                                </div>
                            </>
                        )
                    default:
                        return null
                }
            })()}
            {/* Keep at highest common level */}
            <NotificationManager key={'notificationManager'} />
        </div>
    )
}
